import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import Icon from "../../Icon"
import Button from "../../Button"
import { useClickOutside } from "../../../hooks/useClickOutside"
import { useTranslation } from "react-i18next"
import { useCalendar } from "../../../hooks/useCalendar"
import Input from ".."
import { DaterangeValue } from "../Daterange"
import { ICON } from "../../Icon/data"
import { DateTime } from "luxon"

type Datepicker = {
  value: string | DaterangeValue
  onChange: (val: string) => void
  dateKey?: string
  format?: string
  placeholder?: string
  size?: string
  label?: string
  clamping?: DaterangeValue[]
  onSelect?: () => void
}

const Datepicker = memo(forwardRef(({
  value: inputValue, onChange, dateKey, format, placeholder, size = 'l', label, clamping = [], onSelect,
}: Datepicker, ref) => {
  const { t } = useTranslation()
  const dropdownRef = useRef()
  const inputRef = useRef()

  const [isOpen, setIsOpen] = useState<boolean>(false)

  useClickOutside(dropdownRef, () => setIsOpen(false))

  useImperativeHandle(ref, () => ({
    focus: () => (inputRef.current as any)?.focus?.(),
    open: () => setIsOpen(true),
  }), [])

  const value = useMemo(() => typeof inputValue === 'object' ? inputValue[dateKey] : inputValue, [inputValue, dateKey])

  // The other end of the range (eg. 'to' when this Datepicker is 'from'), used to show/preview the range in the calendar
  const rangeAnchor = useMemo(() => {
    if (typeof inputValue !== 'object' || (dateKey !== 'from' && dateKey !== 'to')) return undefined
    const anchorKey = dateKey === 'from' ? 'to' : 'from'
    return inputValue[anchorKey]
  }, [inputValue, dateKey])

  // Make sure the value is always returned so it's not undefined if the user does not change it
  useEffect(() => {
    if (value !== inputValue) onChange(value)
  }, [])

  const usedValue = useMemo(() => {
    if (value && DateTime.fromISO(value).isValid) {
      return DateTime.fromISO(value).toISODate()
    } 
    return DateTime.now().toISODate()
  }, [value, t])

  const changeDate = useCallback((date: string) => {
    onChange(date)
    setIsOpen(false)
    onSelect?.()
  }, [onChange, onSelect])

  const { getMonthCalendar } = useCalendar({ date: usedValue, onChange: (val) => changeDate(val), clamping, rangeAnchor })

  const chosenFormat = useMemo(() => {
    if (format) return format
    return 'dd.LL.yyyy'
  }, [format])

  return (
    <div className={`Datepicker${isOpen ? ' active' : ''}`}>
      <div className="Datepicker__input">
        <Input
          ref={inputRef}
          type="datesimple"
          size={size}
          label={label}
          value={usedValue}
          format={chosenFormat}
          placeholder={placeholder}
          onInput={(val) => onChange(val)}
        />
        <Button
          type="ghost"
          size="s"
          label={<Icon icon={ICON.CALENDAR} />}
          onClick={() => setIsOpen(true)}
        />
      </div>
      {isOpen && (
        <div ref={dropdownRef} className="Datepicker__dropdown">
          {getMonthCalendar}
        </div>
      )}
    </div>
  )
}))

export default Datepicker
